import {
  AnalyticsResourceV2Type,
  AnalyzeExperienceFrontendTinybirdRequestBodyType,
} from "@intelligems/ig-types";
import {
  TinybirdExperimentAnalysisInput,
  TransformedDataset,
} from "@src/api/analytics/types";
import {
  getConversionStageFilterPayload,
  getMaxZScore,
} from "@src/api/analytics/utils";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import {
  getHasAnyUpsellBlocks,
  getUpsellCheckoutBlockIds,
} from "@src/store/slices/checkoutBlocks/selectors";
import { getOnsiteUpsellComponentIds } from "@src/store/slices/experienceComponents";
import { useSelector } from "@src/store";
import { getIncludePostPurchaseOfferBreakdown } from "./get-include-post-purchase-offer-breakdown";

export const useBuildTinybirdPayload = (): {
  build: (
    params: TinybirdExperimentAnalysisInput,
    datasetGroup: TransformedDataset,
    resourceType: AnalyticsResourceV2Type
  ) => AnalyzeExperienceFrontendTinybirdRequestBodyType;
} => {
  const maxZScore = useConfiguration<number>("max_z_score");
  const dashboardFilters = useSelector((state) => state.dashboardFilters);
  const upsellCheckoutBlockIds = useSelector(getUpsellCheckoutBlockIds);
  const hasUpsellBlocks = useSelector(getHasAnyUpsellBlocks());
  const onsiteUpsellComponentIds = useSelector(getOnsiteUpsellComponentIds);

  const {
    compareGroupA,
    compareGroupB,
    pageName,
    experienceId,
    startTime,
    endTime,
    hiddenEndTime,
    selectedAnalyticsProducts,
    analyticsViewType,
    ...restOfDashboardFilters
  } = dashboardFilters;

  return {
    build: (
      {
        experience,
        audience,
        interval,
        topN,
        histogramValues,
        includeTestOrders,
      }: TinybirdExperimentAnalysisInput,
      datasetGroup: TransformedDataset,
      resourceType: AnalyticsResourceV2Type
    ) => {
      const conversionStageFilterPayload = getConversionStageFilterPayload({
        customEvents: dashboardFilters.customEvents,
        reachedCheckoutStage: dashboardFilters.reachedCheckoutStage,
      });

      return {
        ...restOfDashboardFilters,
        baselineVariationId: compareGroupA === "" ? undefined : compareGroupA,
        experienceId: experience.id!,
        endTs: (dashboardFilters.endTime || hiddenEndTime) as
          | string
          | number
          | null,
        startTs: startTime,
        datasetId: datasetGroup.datasetId,
        landingPage: dashboardFilters.landingPage || null,
        landingPageFilters: dashboardFilters.landingPageFilters?.length
          ? dashboardFilters.landingPageFilters
          : undefined,
        landingPageFiltersOperator: dashboardFilters.landingPageFilters?.length
          ? (dashboardFilters.landingPageFiltersOperator ?? "or")
          : undefined,
        landingPageTypes: dashboardFilters.landingPageTypes ?? undefined, // strips null, keeps undefined so the key is omitted from the payload
        sourceSitesOrChannels: dashboardFilters.channels,
        experimentProductsOnly:
          analyticsViewType === "specific_products" ||
          dashboardFilters.selectedAnalyticsProducts.length > 0
            ? null
            : analyticsViewType === "test_orders_only",
        maxZScore: getMaxZScore(
          dashboardFilters.maxZScore,
          dashboardFilters.isZScoreEnabled,
          maxZScore
        ),
        userBehavior: {
          addedToCart:
            dashboardFilters.reachedCheckoutStage === "added_to_cart" || null,
          viewedCheckout: null,
          viewedProductPage: dashboardFilters.userBehavior.viewedProductPage,
          viewedCollectionPage:
            dashboardFilters.userBehavior.viewedCollectionPage,
        },
        showExperimentProductsOnly:
          datasetGroup.datasetId === "variation_products"
            ? (dashboardFilters.showExperimentProductsOnly ?? false)
            : null,
        metrics: datasetGroup.metrics,
        audience: audience,
        interval: interval,
        topN:
          topN ??
          (datasetGroup.datasetId === "variation_products"
            ? (dashboardFilters.topN ?? null)
            : null),
        kind: histogramValues?.kind,
        histogramMetric: histogramValues?.histogramMetric,
        triggeredCustomEvents:
          conversionStageFilterPayload.triggeredCustomEvents,
        resourceType,
        maxNetRevenue: dashboardFilters.maxNetRevenue ?? null,
        minNetRevenue: dashboardFilters.minNetRevenue ?? null,
        maxShippingRevenue: dashboardFilters.maxShippingRevenue ?? null,
        minShippingRevenue: dashboardFilters.minShippingRevenue ?? null,
        shippingMethods: dashboardFilters.shippingMethods ?? [],
        onlyProductIds: dashboardFilters.selectedAnalyticsProducts.map(
          (p) => p.id
        ),
        onlyProductHandles: dashboardFilters.selectedAnalyticsProducts.map(
          (p) => p.handle
        ),
        // Scope checkout upsell metrics to this experience's blocks so an
        // overlapping checkout-upsell experience's upsells aren't attributed
        // here. On the dashboard this rides the test_orders_only toggle, but an
        // export must be scoped unconditionally: its `is_from_checkout_block`
        // column is read as "from THIS test's upsell", and a file has no toggle
        // state to explain why the same experience exported twice disagrees.
        checkoutBlockIds:
          upsellCheckoutBlockIds.length > 0 &&
          (analyticsViewType === "test_orders_only" ||
            resourceType === "exports")
            ? upsellCheckoutBlockIds
            : undefined,
        // Scope onsite upsell metrics to this experience's components so an
        // overlapping onsite-upsell experience's upsells aren't attributed here.
        onsiteUpsellIds:
          onsiteUpsellComponentIds.length > 0
            ? onsiteUpsellComponentIds
            : undefined,
        reachedCheckoutStage: conversionStageFilterPayload.reachedCheckoutStage,
        includePostPurchaseOfferBreakdown: getIncludePostPurchaseOfferBreakdown(
          {
            datasetId: datasetGroup.datasetId,
            hasTestPostPurchase: experience.testTypes.hasTestPostPurchase,
          }
        ),
        includeCheckoutUpsellBreakdown:
          datasetGroup.datasetId === "variation_products" && hasUpsellBlocks
            ? true
            : undefined,
        includeOnsiteUpsellBreakdown:
          datasetGroup.datasetId === "variation_products" &&
          experience.testTypes.hasTestOnsiteUpsell
            ? true
            : undefined,
        ...(includeTestOrders && { includeTestOrders }),
      };
    },
  } as const;
};
